import React, { useEffect, useState } from 'react';
import { Form, Formik, FieldArray } from 'formik';
import { capitalizeWords, customStyle } from '@custom/Utils/utils';
import { getIcon } from '@ohif/ui';
import * as Yup from 'yup';

const TeleServicePriceForm = ({ editConsultation, initialValues, submitForm, edit, setEdit, modalities, studyRegions, radiologistNames, setShowConsultationForm, isSubmitting, existingRadiologists }) => {

    const [modRadioNames, setModRadioNames] = useState([]);

    const consultationSchema = Yup.object().shape({
        doctorId: Yup.string().required('Radiologist is required'),
        services: Yup.array().of(
            Yup.object().shape({
                modality: Yup.string().required('Modality is required'),
                studyRegion: Yup.string().required('Study Region is required'),
                price: Yup.number()
                    .required('Charge is required')
                    .positive('Charge must be greater than 0')
            })
        )
    });

    useEffect(() => {
        const modList = !edit
            ? radiologistNames?.filter(
            radiologist => !existingRadiologists?.includes(radiologist._id)
        )
        : radiologistNames;
        setModRadioNames(modList);
    }, [existingRadiologists, radiologistNames])

    // Return modalities that don't create duplicate combinations
    const getAvailableModalities = (values, currentIndex, currentStudyRegion) => {
        // Get existing combinations excluding the current row
        const existingCombinations = values.services
            .filter((_, index) => index !== currentIndex)
            .map(service => ({
                modality: service.modality,
                studyRegion: service.studyRegion
            }));

        // If no study region is selected yet, return all modalities
        if (!currentStudyRegion) {
            return modalities;
        }

        // Filter out modalities that would create duplicate combinations
        return modalities.filter(modality =>
            !existingCombinations.some(combo =>
                combo.modality === modality &&
                combo.studyRegion === currentStudyRegion
            )
        );
    };

    // Return study regions that don't create duplicate combinations
    const getAvailableStudyRegions = (values, currentIndex, currentModality) => {
        // Get existing combinations excluding the current row
        const existingCombinations = values.services
            .filter((_, index) => index !== currentIndex)
            .map(service => ({
                modality: service.modality,
                studyRegion: service.studyRegion
            }));

        // If no modality is selected yet, return all study regions
        if (!currentModality) {
            return studyRegions;
        }

        // Filter out study regions that would create duplicate combinations
        return studyRegions.filter(region =>
            !existingCombinations.some(combo =>
                combo.modality === currentModality &&
                combo.studyRegion === region
            )
        );
    };

    return (
        <div className="details">
            <div className="form form--innersec">
                <div className="mb-3">
                    <button
                        className=''
                        onClick={() => setShowConsultationForm(false)}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        {getIcon('icon-toolbar-back')}
                        Back
                    </button>
                </div>
                <section className="form__section">
                    <h1 className="form__section__title">
                        {editConsultation ? "Edit Service Price" : "Add Service Price"}
                    </h1>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={consultationSchema}
                        onSubmit={submitForm}
                        enableReinitialize={true}
                    >
                        {({
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            errors,
                            touched
                        }) => {
                            const isFormValid =
                                values.doctorId &&
                                values.services.length > 0 &&
                                values.services.every(charge =>
                                    charge.modality && charge.studyRegion && charge.price && Number(charge.price) > 0
                                );

                            return (
                                <Form onSubmit={handleSubmit}>
                                    <div className="formRow formRow__3">
                                        <div className="formCol mt-1">
                                            <div className="form__Field">
                                                <label className="form__label" htmlFor="doctorName">
                                                    Doctor Name<span className="required-asterisk">*</span>
                                                </label>
                                                <select
                                                    id="doctorName"
                                                    name="doctorName"
                                                    className="form__select form__select--sm"
                                                    onChange={(e) => {
                                                        const selectedRadiologist = modRadioNames?.find((r) => r._id === e.target.value);
                                                        setFieldValue('doctorName', selectedRadiologist?.name || '');
                                                        setFieldValue('doctorId', selectedRadiologist?._id || '');
                                                    }}
                                                    onBlur={handleBlur}
                                                    value={values.doctorId}
                                                    autoComplete="off"
                                                    style={customStyle.select}
                                                    disabled={edit}
                                                >
                                                    <option value="">-Select-</option>
                                                    {modRadioNames?.map((radiologist) => (
                                                        <option key={radiologist._id} value={radiologist._id}>
                                                            {capitalizeWords(radiologist?.name)}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.doctorId && touched.doctorId && (
                                                    <span className="text-red-500 text-sm font-medium">{errors.doctorId}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <FieldArray name="services">
                                        {({ push, remove }) => (
                                            <div>
                                                {values?.services?.map((charge, index) => {
                                                const availableModalities = getAvailableModalities(values, index, charge.studyRegion);
                                                const availableStudyRegions = getAvailableStudyRegions(values, index, charge.modality);
                                                return (<div key={index} className="charge-field-group">
                                                        <div className="formRow formRow__3">
                                                            <div className="formCol">
                                                                <div className="form__Field">
                                                                    <label className="form__label">
                                                                        Modality
                                                                        {index === 0 && (
                                                                            <button
                                                                                type="button"
                                                                                className="text-lg text-[#51A196] ml-2"
                                                                                onClick={() => push({ modality: '', studyRegion: '', price: '' })}
                                                                            >
                                                                                + Add More
                                                                            </button>
                                                                        )}
                                                                        {index > 0 && (
                                                                            <button
                                                                                type="button"
                                                                                className="text-lg text-red-600 ml-2"
                                                                                onClick={() => remove(index)}
                                                                            >
                                                                                - Remove
                                                                            </button>
                                                                        )}
                                                                    </label>
                                                                    <select
                                                                        className="form__select form__select--sm"
                                                                        name={`services.${index}.modality`}
                                                                        value={charge.modality}
                                                                        style={customStyle.select}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                    >
                                                                        <option value="">-Select-</option>
                                                                        {availableModalities?.map((modality) => (
                                                                            <option key={modality} value={modality}>
                                                                                {modality}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {errors.services?.[index]?.modality && (
                                                                    <span className="text-red-500 text-sm font-medium">
                                                                            {errors.services[index].modality}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="formCol">
                                                                <div className="form__Field">
                                                                    <label className="form__label">
                                                                        Study Region
                                                                    </label>
                                                                    <select
                                                                        className="form__select form__select--sm"
                                                                        name={`services.${index}.studyRegion`}
                                                                        value={charge.studyRegion}
                                                                        style={customStyle.select}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                    >
                                                                        <option value="">-Select-</option>
                                                                        {availableStudyRegions?.map((region) => (
                                                                            <option key={region} value={region}>
                                                                                {region}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {errors.services?.[index]?.studyRegion && (
                                                                    <span className="text-red-500 text-sm font-medium">
                                                                            {errors.services[index].studyRegion}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="formCol">
                                                                <div className="form__Field">
                                                                    <label className="form__label">
                                                                        Service Price
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        className="form__input form__input--sm"
                                                                        name={`services.${index}.price`}
                                                                        placeholder="Price"
                                                                        value={charge.price}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            if (/^\d*\.?\d*$/.test(value) || value === '') {
                                                                                setFieldValue(`services.${index}.price`, value);
                                                                            }
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                    {errors.services?.[index]?.price && (
                                                                    <span className="text-red-500 text-sm font-medium">
                                                                            {errors.services[index].price}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                )}
                                            </div>
                                        )}
                                    </FieldArray>

                                    <div className="btn__group">
                                        <button
                                            className="btn btn__primary"
                                            type="submit"
                                            disabled={!isFormValid || isSubmitting}
                                        >
                                            {edit ? "Update" : "Save"}
                                        </button>
                                        <button
                                            className="btn btn__white"
                                            type="button"
                                            onClick={() => {
                                                setShowConsultationForm(false);
                                                setEdit(false);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </section>
            </div>
        </div>
    );
};

export default TeleServicePriceForm;
